import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { redirectToMainDomain, getLoginUrl } from '@/utils/domainUtils';
import { User } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { organizationService, Organization } from '@/firebase/organizationService';
import { getUserSystemRoleForOrganization, isAdminInOrganization, isManagerInOrganization } from '@/utils/roleHelpers';
import GoogleAuth from './GoogleAuth';
import MessageIcon from './messaging/MessageIcon';
import { Building2, ChevronDown, Users, Settings, User as UserIcon, LogOut, CreditCard, Smartphone, HelpCircle, FileText } from 'lucide-react';

const DemoAuth: React.FC = () => {
  const { login, logout, isAuthenticated, user } = useAuth();
  const { currentOrganization, setCurrentOrganization } = useOrganization();
  const [isLoading, setIsLoading] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleDemoLogin = () => {
    setIsLoading(true);
    
    // Simulate API call delay
    setTimeout(() => {
      const demoUser: User = {
        id: 'demo-user-123',
        name: 'Demo User',
        email: 'demo@example.com',
        picture: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
        verified_email: true,
      };
      
      login(demoUser);
      setIsLoading(false);
      redirectToMainDomain('/home'); // Navigate to main domain after login
    }, 1000);
  };

  // Load organizations and user role when user is authenticated or when currentOrganization changes
  React.useEffect(() => {
    if (isAuthenticated && user?.id) {
      loadUserOrganizations();
      loadUserRole();
    }
  }, [isAuthenticated, user?.id, currentOrganization?.id, currentOrganization?.name]);

  const loadUserRole = async () => {
    if (user?.id && currentOrganization?.id) {
      try {
        console.log(`[DemoAuth] Loading user role for org: ${currentOrganization.id} (${currentOrganization.name})`);
        
        // Fetch from Firestore to get organization-specific role
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const organizationId = currentOrganization.id;
          
          console.log(`[DemoAuth] User data:`, {
            organizationRoles: userData.organizationRoles,
            organizationId: organizationId,
            legacyRole: userData.role
          });
          
          // Debug: Log the full organizationRoles structure
          if (userData.organizationRoles) {
            console.log(`[DemoAuth] 📋 Full organizationRoles object:`, JSON.stringify(userData.organizationRoles, null, 2));
            console.log(`[DemoAuth] 📋 organizationRoles keys:`, Object.keys(userData.organizationRoles));
            console.log(`[DemoAuth] 📋 Looking for org ID: ${organizationId}`);
            console.log(`[DemoAuth] 📋 organizationRoles[${organizationId}]:`, userData.organizationRoles[organizationId]);
          } else {
            console.warn(`[DemoAuth] ⚠️ No organizationRoles found in user data`);
          }
          
          // Get organization-specific system role
          const systemRole = getUserSystemRoleForOrganization(userData, organizationId);
          
          console.log(`[DemoAuth] ✅ User role for org ${organizationId}: ${systemRole}`);
          console.log(`[DemoAuth] Will show User Management: ${systemRole === 'admin' || systemRole === 'manager'}`);
          
          setUserRole(systemRole || 'user'); // Default to 'user' if not set
        } else {
          console.log('⚠️ User document not found in Firestore');
          // Fallback to user context role if available
          setUserRole(user.role || 'user');
        }
      } catch (error) {
        console.error('Error fetching user role:', error);
        // Fallback to user context role if available
        setUserRole(user.role || 'user');
      }
    } else if (user?.id && !currentOrganization?.id) {
      // No organization selected - use legacy role from user context
      console.log('[DemoAuth] No organization selected, using legacy role:', user.role);
      setUserRole(user.role || 'user');
    }
  };

  const loadUserOrganizations = async () => {
    if (!user?.id) return;

    try {
      console.log('🔄 Loading user organizations for user:', user.id, user.email);
      const orgMap = new Map<string, Organization>();
      
      // Load current organization from context (always up-to-date)
      if (currentOrganization?.id) {
        orgMap.set(currentOrganization.id, currentOrganization);
        console.log('📋 Added current organization to map:', currentOrganization.name);
      }
      
      // Get user data to access organizationIds array
      const { userService } = await import('@/firebase/database');
      const userResult = await userService.getUser(user.id);
      
      if (userResult.success && userResult.data) {
        const userData = userResult.data;
        const userOrgIds: string[] = [];
        
        console.log('📋 User data from Firestore:', {
          organizationId: userData.organizationId,
          organizationIds: userData.organizationIds,
          hasOrganizationIds: !!userData.organizationIds,
          organizationIdsType: Array.isArray(userData.organizationIds) ? 'array' : typeof userData.organizationIds
        });
        
        // Get organizationIds array (support multiple organizations)
        if (userData.organizationIds && Array.isArray(userData.organizationIds)) {
          userOrgIds.push(...userData.organizationIds);
          console.log('✅ Found organizationIds array:', userOrgIds);
        }
        
        // Backward compatibility: if user has single organizationId, add it
        if (userData.organizationId && !userOrgIds.includes(userData.organizationId)) {
          userOrgIds.push(userData.organizationId);
          console.log('✅ Added organizationId (backward compat):', userData.organizationId);
        }
        
        console.log('📋 Total organization IDs to load:', userOrgIds.length, userOrgIds);
        
        // Load all organizations user belongs to
        if (userOrgIds.length > 0) {
          const orgsResult = await organizationService.getOrganizationsByIds(userOrgIds);
          if (orgsResult.success && orgsResult.data) {
            console.log('✅ Loaded organizations from IDs:', orgsResult.data.length, orgsResult.data.map(o => o.name));
            orgsResult.data.forEach(org => {
              if (org.id && !orgMap.has(org.id)) {
                orgMap.set(org.id, org);
              }
            });
          } else {
            console.error('❌ Failed to load organizations:', orgsResult.error);
          }
        } else {
          console.warn('⚠️ No organization IDs found for user');
        }
      } else {
        console.error('❌ Failed to get user data:', userResult.error);
      }
      
      // Also load organizations owned by user (in case they're not in organizationIds yet)
      const ownedResult = await organizationService.getOrganizationsByOwner(user.id);
      if (ownedResult.success && ownedResult.data) {
        console.log('✅ Loaded owned organizations:', ownedResult.data.length, ownedResult.data.map(o => o.name));
        ownedResult.data.forEach(org => {
          if (org.id && !orgMap.has(org.id)) {
            orgMap.set(org.id, org);
          }
        });
      }
      
      // Convert Map to array to avoid duplicates
      const uniqueOrgs = Array.from(orgMap.values());
      console.log('✅ Final organizations list:', uniqueOrgs.length, uniqueOrgs.map(o => o.name));
      setOrganizations(uniqueOrgs);
    } catch (error: any) {
      console.error('❌ Error loading organizations:', error);
    }
  };

  const handleOrganizationChange = async (orgId: string) => {
    const selectedOrg = organizations.find(o => o.id === orgId);
    if (selectedOrg) {
      await setCurrentOrganization(selectedOrg);
      setShowOrgDropdown(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
        <span className="text-sm text-gray-600">Signing in...</span>
      </div>
    );
  }

  if (isAuthenticated && user) {
    return (
      <div className="flex items-center space-x-3">
        {/* Message Icon - before organization selector */}
        <MessageIcon />
        
        {/* Organization Selector - only show if organizations exist */}
        {organizations.length > 0 && (
          <div className="relative">
            <button
              onClick={() => setShowOrgDropdown(!showOrgDropdown)}
              className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              <Building2 className="w-4 h-4 text-gray-600" />
              <span className="hidden sm:inline text-sm font-medium text-gray-900">
                {currentOrganization?.name || 'Select Organization'}
              </span>
              <ChevronDown className="w-4 h-4 text-gray-600" />
            </button>
            
            {showOrgDropdown && (
              <>
                {/* Backdrop */}
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowOrgDropdown(false)}
                />
                
                {/* Dropdown Menu */}
                <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-20">
                  <div className="py-2">
                    {organizations.map((org) => (
                      <button
                        key={org.id}
                        onClick={() => org.id && handleOrganizationChange(org.id)}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors ${
                          currentOrganization?.id === org.id 
                            ? 'bg-blue-50 text-blue-700 font-medium' 
                            : 'text-gray-700'
                        }`}
                      >
                        <div className="font-medium">{org.name}</div>
                        {org.description && (
                          <div className="text-xs text-gray-500 mt-0.5">{org.description}</div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* User Profile with Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            <UserIcon className="w-4 h-4 text-gray-600" />
            <div className="hidden sm:block text-left">
              <p className="text-sm font-medium text-gray-900">{user.name}</p>
              <p className="text-xs text-gray-600">{user.email}</p>
            </div>
            <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
          </button>
          
          {showUserMenu && (
            <>
              {/* Backdrop */}
              <div 
                className="fixed inset-0 z-10" 
                onClick={() => setShowUserMenu(false)}
              />
              
              {/* Dropdown Menu */}
              <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-20">
                <div className="py-2">
                  {/* User Info Header */}
                  <div className="px-4 py-2 border-b border-gray-200">
                    <p className="text-sm font-medium text-gray-900">{user.name}</p>
                    <p className="text-xs text-gray-600">{user.email}</p>
                    {userRole && (
                      <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                        {userRole}
                      </span>
                    )}
                  </div>
                  
                  {/* Menu Items */}
                  <div className="py-1">
                    {/* Always show Organizations if user has no organization, or if user is admin/manager for current org */}
                    {(!currentOrganization?.id || userRole === 'admin' || userRole === 'manager') && (
                      <button
                        onClick={() => {
                          navigate('/organizations');
                          setShowUserMenu(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                      >
                        <Building2 className="w-4 h-4" />
                        Organizations
                      </button>
                    )}
                    {/* User Management - only for admin/manager */}
                    {(() => {
                      const shouldShow = userRole === 'admin' || userRole === 'manager';
                      console.log(`[DemoAuth] Rendering menu - userRole: ${userRole}, shouldShowUserManagement: ${shouldShow}, currentOrg: ${currentOrganization?.id}`);
                      return shouldShow && (
                        <button
                          onClick={() => {
                            navigate('/user-management');
                            setShowUserMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                        >
                          <Users className="w-4 h-4" />
                          User Management
                        </button>
                      );
                    })()}
                    {/* Subscription - only for admin */}
                    {userRole === 'admin' && (
                      <button
                        onClick={() => {
                          navigate('/subscription');
                          setShowUserMenu(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                      >
                        <CreditCard className="w-4 h-4" />
                        Subscription
                      </button>
                    )}
                    {/* Audit Log - only for admin or manager */}
                    {(userRole === 'admin' || userRole === 'manager') && (
                      <button
                        onClick={() => {
                          navigate('/audit-log');
                          setShowUserMenu(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                      >
                        <FileText className="w-4 h-4" />
                        Audit Log
                      </button>
                    )}
                    <button
                      onClick={() => {
                        navigate('/settings');
                        setShowUserMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                    >
                      <Settings className="w-4 h-4" />
                      Settings
                    </button>
                    <button
                      onClick={() => {
                        navigate('/mobile-app');
                        setShowUserMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                    >
                      <Smartphone className="w-4 h-4" />
                      Mobile App
                    </button>
                    <button
                      onClick={() => {
                        navigate('/help');
                        setShowUserMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                    >
                      <HelpCircle className="w-4 h-4" />
                      Help
                    </button>
                    <div className="border-t border-gray-200 my-1" />
                    <button
                      onClick={() => {
                        logout();
                        setShowUserMenu(false);
                        window.location.href = getLoginUrl();
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-2">
      <button
        onClick={handleDemoLogin}
        className="btn-primary flex items-center space-x-2"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
        </svg>
        <span>Demo Login</span>
      </button>
      <span className="text-xs text-gray-500">or</span>
      <GoogleAuth />
    </div>
  );
};

export default DemoAuth;
